import { Disclosure } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { FC } from "react";
// Inline HTML sanitization to avoid module loading issues
const sanitizeHtml = (html: string): string => {
  if (!html || typeof window === 'undefined') return html || '';
  
  // Simple text-based sanitization for safety
  let sanitized = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, '')
    .replace(/src\s*=\s*["']data:[^"']*["']/gi, '');
  
  return sanitized;
};

const DEFAULT_DATA = [
  {
    name: "Description",
    content: "",
  },
  {
    name: "Fabric + Care",
    content: "",
  },
  {
    name: "How it Fits",
    content: "",
  },
  {
    name: "FAQ",
    content: "",
  },
];

interface Props {
  panelClassName?: string;
  data?: typeof DEFAULT_DATA;
  productData?: any;
  isLoading?: boolean;
}

const AccordionInfo: FC<Props> = ({
  panelClassName = "p-4 pt-3 last:pb-0 text-slate-600 text-sm dark:text-slate-300 leading-6",
  data = DEFAULT_DATA,
  productData,
  isLoading = false,
}) => {
  // Get commercial_description from API data
  const commercialDescription = productData?.data?.commercial_description;
  
  // Sanitize HTML content to prevent XSS attacks
  const sanitizedHtml = commercialDescription 
    ? sanitizeHtml(commercialDescription)
    : null;

  // Process data to use dynamic content for Description
  const processedData = data
    .map((item) => {
      if (item.name === "Description") {
        return {
          ...item,
          content: isLoading 
            ? `<div class="space-y-2">
                <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-3/4"></div>
                <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-1/2"></div>
              </div>`
            : sanitizedHtml || item.content
        };
      }
      return item;
    })
    .filter((item) => {
      // Only show items that have content (not empty strings)
      return item.content && item.content.trim() !== "";
    });

  return (
    <div className="w-full rounded-2xl space-y-2.5">
      {/* ============ */}
      {processedData.map((item, index) => {
        return (
          <Disclosure key={index} defaultOpen={index < 2}>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex items-center justify-between w-full px-4 py-2 font-medium text-left bg-slate-100/80 hover:bg-slate-200/60 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75 ">
                  <span>{item.name}</span>
                  {!open ? (
                    <PlusIcon className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                  ) : (
                    <MinusIcon className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                  )}
                </Disclosure.Button>
                <Disclosure.Panel
                  className={panelClassName}
                  as="div"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                ></Disclosure.Panel>
              </>
            )}
          </Disclosure>
        );
      })}

      {/* ============ */}
    </div>
  );
};

export default AccordionInfo;
